import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Search,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  MessageSquare,
  Calendar,
  User,
  Upload,
  Eye,
  Send,
  Target,
  GraduationCap,
  Plane,
  Home,
  DollarSign,
  Briefcase,
  RefreshCw,
  Loader2,
  ArrowRight,
  Sparkles,
  TrendingUp,
  ChevronRight,
} from 'lucide-react';
import { serviceRequestAPI, taskAPI } from '../../services/api';
import {
  initSocket,
  onServiceRequestUpdated,
  offServiceRequestUpdated,
  onTaskCreated,
  offTaskCreated,
  onTaskUpdated,
  offTaskUpdated,
} from '../../services/socket';
import FileUpload from '../../components/shared/FileUpload';
import TaskCard from '../../components/shared/TaskCard';
import ServiceChat from '../../components/services/ServiceChat';
import { useInteractionMode } from '../../hooks/useInteractionMode';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const SERVICE_TYPE_CONFIG = {
  PROFILE_ASSESSMENT: {
    label: 'Profile Assessment',
    icon: Target,
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-600',
  },
  UNIVERSITY_SHORTLISTING: {
    label: 'Preapplication Support',
    icon: GraduationCap,
    color: 'from-purple-500 to-purple-600',
    bgLight: 'bg-purple-50',
    textColor: 'text-purple-600',
  },
  APPLICATION_ASSISTANCE: {
    label: 'Apply University',
    icon: FileText,
    color: 'from-pink-500 to-pink-600',
    bgLight: 'bg-pink-50',
    textColor: 'text-pink-600',
  },
  VISA_GUIDANCE: {
    label: 'Visa & Interview Support',
    icon: Plane,
    color: 'from-orange-500 to-orange-600',
    bgLight: 'bg-orange-50',
    textColor: 'text-orange-600',
  },
  SCHOLARSHIP_SEARCH: {
    label: 'Ticket & Travel Support',
    icon: DollarSign,
    color: 'from-green-500 to-green-600',
    bgLight: 'bg-green-50',
    textColor: 'text-green-600',
  },
  LOAN_ASSISTANCE: {
    label: 'Education Loan',
    icon: DollarSign,
    color: 'from-emerald-500 to-emerald-600',
    bgLight: 'bg-emerald-50',
    textColor: 'text-emerald-600',
  },
  ACCOMMODATION_HELP: {
    label: 'Find Accommodation',
    icon: Home,
    color: 'from-cyan-500 to-cyan-600',
    bgLight: 'bg-cyan-50',
    textColor: 'text-cyan-600',
  },
  PRE_DEPARTURE_ORIENTATION: {
    label: 'Find Jobs Abroad',
    icon: Briefcase,
    color: 'from-indigo-500 to-indigo-600',
    bgLight: 'bg-indigo-50',
    textColor: 'text-indigo-600',
  },
};

const STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: {
    label: 'Pending Assignment',
    color: 'bg-slate-100 text-slate-700 border-slate-200',
    icon: Clock,
    dotColor: 'bg-slate-400',
  },
  ASSIGNED: {
    label: 'Assigned',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: User,
    dotColor: 'bg-blue-500',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: RefreshCw,
    dotColor: 'bg-amber-500',
  },
  ON_HOLD: {
    label: 'On Hold',
    color: 'bg-orange-50 text-orange-700 border-orange-200',
    icon: AlertCircle,
    dotColor: 'bg-orange-500',
  },
  COMPLETED: {
    label: 'Completed',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: CheckCircle,
    dotColor: 'bg-emerald-500',
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'bg-red-50 text-red-700 border-red-200',
    icon: AlertCircle,
    dotColor: 'bg-red-500',
  },
};

export default function TrackServices() {
  const navigate = useNavigate();
  const { isRepManaged, canSubmitTasks, canSendMessages } = useInteractionMode();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  // Modals
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [submitTaskOpen, setSubmitTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);

  // Task submission
  const [submissionText, setSubmissionText] = useState('');
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Message sending
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const [requestsRes, tasksRes] = await Promise.all([
        serviceRequestAPI.getAll(),
        taskAPI.getAll(),
      ]);
      setServiceRequests(requestsRes.serviceRequests || []);
      setTasks(tasksRes.tasks || []);
    } catch (error) {
      toast.error('Failed to fetch service data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initialize socket and set up listeners
  useEffect(() => {
    fetchData();

    // Initialize socket connection
    initSocket();

    // Listen for service request updates
    onServiceRequestUpdated(updatedRequest => {
      setServiceRequests(prev => {
        const index = prev.findIndex(
          r => r.serviceRequestId === updatedRequest.serviceRequestId,
        );
        if (index >= 0) {
          const newRequests = [...prev];
          newRequests[index] = updatedRequest;
          return newRequests;
        }
        return prev;
      });

      // Update selected request if it's open
      setSelectedRequest(prev => {
        if (prev && prev.serviceRequestId === updatedRequest.serviceRequestId) {
          return updatedRequest;
        }
        return prev;
      });

      toast.info('Service request updated');
    });

    // Listen for new tasks
    onTaskCreated(newTask => {
      setTasks(prev => [newTask, ...prev]);
      toast.info(`New task assigned: ${newTask.title}`);
    });

    // Listen for task updates
    onTaskUpdated(updatedTask => {
      setTasks(prev => {
        const index = prev.findIndex(t => t.taskId === updatedTask.taskId);
        if (index >= 0) {
          const newTasks = [...prev];
          newTasks[index] = updatedTask;
          return newTasks;
        }
        return prev;
      });
      toast.info('Task updated');
    });

    // Cleanup on unmount
    return () => {
      offServiceRequestUpdated();
      offTaskCreated();
      offTaskUpdated();
    };
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData(true);
  };

  const handleViewDetails = async request => {
    // Navigate to the service detail page
    navigate(`/dashboard/service/${request.serviceRequestId}`);
  };

  const handleSubmitTask = task => {
    if (isRepManaged) {
      toast.error('Your account is managed by a representative. Please contact them for task submissions.');
      return;
    }
    setSelectedTask(task);
    setSubmissionText('');
    setSubmissionFiles([]);
    setSubmitTaskOpen(true);
  };

  const handleTaskSubmission = async () => {
    if (!submissionText.trim() && submissionFiles.length === 0) {
      toast.error('Please provide text or upload files');
      return;
    }

    setSubmitting(true);
    try {
      await taskAPI.submit(
        selectedTask.taskId,
        submissionText,
        submissionFiles,
      );
      toast.success('Task submitted successfully!');
      setSubmitTaskOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUploadComplete = uploadedFiles => {
    setSubmissionFiles(prev => [...prev, ...uploadedFiles]);
  };

  const handleOpenMessageModal = request => {
    setSelectedRequest(request);
    setMessageText('');
    setMessageModalOpen(true);
  };

  const handleSendMessage = async () => {
    if (isRepManaged) {
      toast.error('Your account is managed by a representative. Please contact them to communicate with your counselor.');
      return;
    }
    if (!messageText.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingMessage(true);
    try {
      await serviceRequestAPI.addNote(
        selectedRequest.serviceRequestId,
        messageText,
        false,
      );
      toast.success('Message sent successfully!');
      setMessageModalOpen(false);
      setMessageText('');
      // Refresh the selected request to show the new note
      const response = await serviceRequestAPI.getById(
        selectedRequest.serviceRequestId,
      );
      setSelectedRequest(response.serviceRequest);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const getRequestTasks = serviceRequestId => {
    return tasks.filter(t => t.serviceRequestId === serviceRequestId);
  };

  const filteredRequests = serviceRequests.filter(request => {
    const serviceConfig = SERVICE_TYPE_CONFIG[request.serviceType] || {};
    const matchesSearch = serviceConfig.label
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());

    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'active')
      return (
        matchesSearch && ['ASSIGNED', 'IN_PROGRESS'].includes(request.status)
      );
    if (activeTab === 'pending')
      return matchesSearch && request.status === 'PENDING_ADMIN_ASSIGNMENT';
    if (activeTab === 'completed')
      return matchesSearch && request.status === 'COMPLETED';

    return matchesSearch;
  });

  const stats = {
    total: serviceRequests.length,
    pending: serviceRequests.filter(
      r => r.status === 'PENDING_ADMIN_ASSIGNMENT',
    ).length,
    active: serviceRequests.filter(r =>
      ['ASSIGNED', 'IN_PROGRESS'].includes(r.status),
    ).length,
    completed: serviceRequests.filter(r => r.status === 'COMPLETED').length,
    pendingTasks: tasks.filter(t =>
      ['PENDING', 'IN_PROGRESS', 'REVISION_REQUIRED'].includes(t.status),
    ).length,
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)] bg-gray-50">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-800 mt-6 font-semibold text-lg">
              Loading your services...
            </p>
            <p className="text-gray-500 text-sm mt-1">Please wait a moment</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="track-services-page">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-[#D8EFFF] p-6 lg:p-8 shadow-sm border border-white/40">
          <div className="absolute inset-0 bg-grid-slate-900/[0.03]" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-sky-400/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-400/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/60 backdrop-blur-md border border-white/50 shadow-sm">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-xs font-medium text-emerald-600">
                    {stats.active} Active
                  </span>
                </div>
              </div>

              <h1 className="text-2xl lg:text-3xl font-outfit font-bold text-slate-800">
                Track Services
              </h1>

              <p className="text-slate-600 mt-2 max-w-lg">
                Monitor the progress of your service requests and stay updated
                on your study abroad journey.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={refreshing}
                className="rounded-xl bg-white/70 border-white/60 text-slate-700 hover:bg-white/90 hover:text-slate-900 transition-all shadow-sm"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>

              <Button
                onClick={() => navigate('/dashboard/services')}
                className="rounded-xl bg-cyan-600 text-black hover:bg-blue-500 shadow-md"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Explore All Services
              </Button>
            </div>
          </div>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Total Requests */}
          <Card className="group relative bg-gradient-to-br from-sky-100 to-sky-50/80 border border-sky-200/80 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 min-h-[88px]">
            <div className="relative p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/20 shrink-0">
                <FileText className="w-5 h-5 text-white" />
              </div>

              <div className="flex-1">
                <p className="text-xl font-bold text-slate-900 leading-none">
                  {stats.total}
                </p>
                <p className="text-xs text-slate-500 font-medium mt-1">
                  Total Requests
                </p>
              </div>

              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-sky-500 group-hover:translate-x-1 transition-all" />
            </div>
          </Card>

          {/* Pending */}
          <Card className="group relative bg-gradient-to-br from-pink-100 to-pink-50 border border-pink-200/80 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 min-h-[88px]">
            <div className="relative p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-pink-500/20 shrink-0">
                <Clock className="w-5 h-5 text-white" />
              </div>

              <div className="flex-1">
                <p className="text-xl font-bold text-slate-900 leading-none">
                  {stats.pending}
                </p>
                <p className="text-xs text-slate-500 font-medium mt-1">
                  Pending
                </p>
              </div>

              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-1 transition-all" />
            </div>
          </Card>

          {/* Active */}
          <Card className="group relative bg-gradient-to-br from-amber-100 to-amber-50/80 border border-amber-200/80 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 min-h-[88px]">
            <div className="relative p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20 shrink-0">
                <RefreshCw className="w-5 h-5 text-white" />
              </div>

              <div className="flex-1">
                <p className="text-xl font-bold text-slate-900 leading-none">
                  {stats.active}
                </p>
                <p className="text-xs text-slate-500 font-medium mt-1">
                  Active
                </p>
              </div>

              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
            </div>
          </Card>

          {/* Completed */}
          <Card className="group relative bg-gradient-to-br from-emerald-100 to-emerald-50/80 border border-emerald-200/80 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 min-h-[88px]">
            <div className="relative p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20 shrink-0">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>

              <div className="flex-1">
                <p className="text-xl font-bold text-slate-900 leading-none">
                  {stats.completed}
                </p>
                <p className="text-xs text-slate-500 font-medium mt-1">
                  Completed
                </p>
              </div>

              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
            </div>
          </Card>

          {/* Tasks Due */}
          <Card className="group relative bg-gradient-to-br from-violet-100 to-violet-50/80 border border-violet-200/80 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 min-h-[88px] col-span-2 lg:col-span-1">
            <div className="relative p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/20 shrink-0">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>

              <div className="flex-1">
                <p className="text-xl font-bold text-slate-900 leading-none">
                  {stats.pendingTasks}
                </p>
                <p className="text-xs text-slate-500 font-medium mt-1">
                  Tasks Due
                </p>
              </div>

              {stats.pendingTasks > 0 && (
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-violet-500" />
                </span>
              )}
            </div>
          </Card>
        </div>

        {/* Tabs and Search */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full lg:w-auto"
          >
            <TabsList className="flex gap-1 bg-slate-100 border border-slate-200 rounded-lg p-1">
              {/* All */}
              <TabsTrigger
                value="all"
                className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium
        text-slate-600
        data-[state=active]:bg-white
        data-[state=active]:text-slate-900
        data-[state=active]:shadow-sm
        data-[state=active]:border
        data-[state=active]:border-slate-200"
              >
                All
                <span
                  className="min-w-[20px] text-center text-xs font-semibold
        bg-slate-200 text-slate-700
        data-[state=active]:bg-slate-900
        data-[state=active]:text-white
        px-1.5 py-0.5 rounded"
                >
                  {stats.total}
                </span>
              </TabsTrigger>

              {/* Active */}
              <TabsTrigger
                value="active"
                className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium
        text-slate-600
        data-[state=active]:bg-white
        data-[state=active]:text-slate-900
        data-[state=active]:shadow-sm
        data-[state=active]:border
        data-[state=active]:border-slate-200"
              >
                Active
                <span
                  className="min-w-[20px] text-center text-xs font-semibold
        bg-slate-200 text-slate-700
        data-[state=active]:bg-slate-900
        data-[state=active]:text-white
        px-1.5 py-0.5 rounded"
                >
                  {stats.active}
                </span>
              </TabsTrigger>

              {/* Pending */}
              <TabsTrigger
                value="pending"
                className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium
        text-slate-600
        data-[state=active]:bg-white
        data-[state=active]:text-slate-900
        data-[state=active]:shadow-sm
        data-[state=active]:border
        data-[state=active]:border-slate-200"
              >
                Pending
                <span
                  className="min-w-[20px] text-center text-xs font-semibold
        bg-slate-200 text-slate-700
        data-[state=active]:bg-slate-900
        data-[state=active]:text-white
        px-1.5 py-0.5 rounded"
                >
                  {stats.pending}
                </span>
              </TabsTrigger>

              {/* Completed */}
              <TabsTrigger
                value="completed"
                className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium
        text-slate-600
        data-[state=active]:bg-white
        data-[state=active]:text-slate-900
        data-[state=active]:shadow-sm
        data-[state=active]:border
        data-[state=active]:border-slate-200"
              >
                Completed
                <span
                  className="min-w-[20px] text-center text-xs font-semibold
        bg-slate-200 text-slate-700
        data-[state=active]:bg-slate-900
        data-[state=active]:text-white
        px-1.5 py-0.5 rounded"
                >
                  {stats.completed}
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />

            <Input
              placeholder="Search services"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="
      h-10 pl-10 pr-3
      text-sm
      bg-white
      border border-slate-200
      rounded-md
      text-slate-900
      placeholder:text-slate-400
      focus-visible:ring-2
      focus-visible:ring-slate-900/10
      focus-visible:border-slate-300
      transition-colors
    "
            />
          </div>
        </div>

        {/* Service Requests List */}
        {filteredRequests.length === 0 ? (
          <Card className="border border-slate-100 shadow-sm bg-white rounded-2xl">
            <div className="p-12 lg:p-16 text-center">
              <div className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-slate-300" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                No Service Requests Found
              </h3>
              <p className="text-slate-400 mb-6 max-w-sm mx-auto">
                You haven't applied for any services yet. Start exploring our
                services to begin your journey.
              </p>
              <Button
                onClick={() => navigate('/dashboard/services')}
                className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 hover:opacity-90 shadow-lg shadow-sky-500/25"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Explore Services
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredRequests.map(request => {
              const serviceConfig =
                SERVICE_TYPE_CONFIG[request.serviceType] || {};
              const statusConfig =
                STATUS_CONFIG[request.status] ||
                STATUS_CONFIG.PENDING_ADMIN_ASSIGNMENT;

              const ServiceIcon = serviceConfig.icon || FileText;
              const requestTasks = getRequestTasks(request.serviceRequestId);
              const pendingTasks = requestTasks.filter(t =>
                ['PENDING', 'IN_PROGRESS', 'REVISION_REQUIRED'].includes(
                  t.status,
                ),
              );

              const counselor =
                request.counselor || request.assignedCounselorInfo;
              const agent = request.agent || request.assignedAgentInfo;

              const isUnassigned =
                request.status === 'PENDING_ADMIN_ASSIGNMENT';
              const isDisabled = isUnassigned || request.status === 'CANCELLED';

              return (
                <Card
                  key={request.serviceRequestId}
                  className={`relative border border-slate-100 bg-white rounded-xl transition-all duration-200 ${
                    isDisabled
                      ? 'opacity-70'
                      : 'hover:shadow-lg hover:border-slate-200'
                  }`}
                >
                  {/* Accent line */}
                  <div
                    className={`absolute left-0 top-0 h-full w-1 rounded-l-xl ${
                      serviceConfig.color
                        ? serviceConfig.color.replace('to-', 'from-')
                        : 'bg-slate-300'
                    }`}
                  />

                  <div className="p-5">
                    {/* Header */}
                    <div className="flex flex-col lg:flex-row justify-between gap-4">
                      {/* Service */}
                      <div className="flex gap-4">
                        <div className="w-11 h-11 rounded-xl bg-slate-50 flex items-center justify-center border border-slate-100">
                          <ServiceIcon className="w-5 h-5 text-slate-500" />
                        </div>

                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold text-slate-900">
                              {serviceConfig.label || request.serviceType}
                            </h3>

                            <Badge className="text-xs font-medium border px-2 py-0.5">
                              {statusConfig.label}
                            </Badge>

                            {!isDisabled && pendingTasks.length > 0 && (
                              <Badge className="text-xs bg-rose-50 text-rose-700 border border-rose-200">
                                {pendingTasks.length} due
                              </Badge>
                            )}
                          </div>

                          <div className="mt-1 flex gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {formatDistanceToNow(
                                new Date(
                                  request.appliedAt || request.createdAt,
                                ),
                                { addSuffix: true },
                              )}
                            </span>

                            {counselor && !isUnassigned && (
                              <span className="flex items-center gap-1">
                                <User className="w-3.5 h-3.5" />
                                {counselor.firstName} {counselor.lastName}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {!isDisabled && counselor && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenMessageModal(request)}
                            className="text-xs"
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Message
                          </Button>
                        )}

                        <Button
                          size="sm"
                          disabled={isDisabled}
                          onClick={() =>
                            !isDisabled && handleViewDetails(request)
                          }
                          className={`text-xs ${
                            isDisabled
                              ? 'border border-slate-200 text-slate-400'
                              : ''
                          }`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {isDisabled ? 'Pending' : 'View'}
                        </Button>
                      </div>
                    </div>

                    {/* Progress */}
                    {!isUnassigned && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-slate-600 mb-1">
                          <span>Progress</span>
                          <span className="font-medium">
                            {request.progress || 0}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded">
                          <div
                            className="h-full bg-slate-700 rounded"
                            style={{ width: `${request.progress || 0}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Counselor */}
                    {!isDisabled && counselor && (
                      <div className="mt-4 p-3 border border-slate-200 rounded-md bg-slate-50 flex justify-between items-center">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">
                            Counselor
                          </p>
                          <p className="text-sm font-medium text-slate-900">
                            {counselor.firstName} {counselor.lastName}
                          </p>
                          <p className="text-xs text-slate-500">
                            {counselor.email}
                          </p>
                        </div>

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenMessageModal(request)}
                          className="text-xs"
                        >
                          Chat
                        </Button>
                      </div>
                    )}

                    {/* Pending Tasks */}
                    {!isDisabled && pendingTasks.length > 0 && (
                      <div className="mt-4 border border-slate-200 rounded-md">
                        <div className="px-3 py-2 border-b text-xs font-semibold text-slate-700 bg-slate-50">
                          Pending Tasks ({pendingTasks.length})
                        </div>

                        <div className="divide-y">
                          {pendingTasks.slice(0, 2).map(task => (
                            <div
                              key={task.taskId}
                              className="p-3 flex justify-between items-center"
                            >
                              <span className="text-sm text-slate-700">
                                {task.title}
                              </span>
                              <Button
                                size="sm"
                                onClick={() => handleSubmitTask(task)}
                                className="text-xs"
                              >
                                Submit
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Service Request Details Modal */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-0 shadow-2xl rounded-2xl">
            {selectedRequest && (
              <>
                <DialogHeader className="pb-4 border-b">
                  <DialogTitle className="flex items-center gap-3 text-xl">
                    <div
                      className={`w-10 h-10 bg-gradient-to-br ${SERVICE_TYPE_CONFIG[selectedRequest.serviceType]?.color || 'from-slate-400 to-slate-500'} rounded-xl flex items-center justify-center`}
                    >
                      {(() => {
                        const Icon =
                          SERVICE_TYPE_CONFIG[selectedRequest.serviceType]
                            ?.icon || FileText;
                        return <Icon className="w-5 h-5 text-white" />;
                      })()}
                    </div>
                    <div>
                      <span>
                        {SERVICE_TYPE_CONFIG[selectedRequest.serviceType]
                          ?.label || selectedRequest.serviceType}
                      </span>
                      <Badge
                        className={`${STATUS_CONFIG[selectedRequest.status]?.color} border font-medium ml-3`}
                      >
                        {STATUS_CONFIG[selectedRequest.status]?.label}
                      </Badge>
                    </div>
                  </DialogTitle>
                  <DialogDescription className="text-slate-500">
                    Request ID:{' '}
                    <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
                      {selectedRequest.serviceRequestId}
                    </span>
                  </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="overview" className="mt-6">
                  <TabsList className="grid w-full grid-cols-4 bg-slate-100 p-1 rounded-xl">
                    <TabsTrigger value="overview" className="rounded-lg">
                      Overview
                    </TabsTrigger>
                    <TabsTrigger value="tasks" className="rounded-lg">
                      Tasks (
                      {getRequestTasks(selectedRequest.serviceRequestId).length}
                      )
                    </TabsTrigger>
                    <TabsTrigger value="messages" className="rounded-lg">
                      Messages
                    </TabsTrigger>
                    <TabsTrigger value="history" className="rounded-lg">
                      History
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-5 mt-6">
                    {/* Progress */}
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex justify-between items-center mb-3">
                        <p className="text-sm font-semibold text-slate-700">
                          Overall Progress
                        </p>
                        <span className="text-lg font-bold text-slate-900">
                          {selectedRequest.progress || 0}%
                        </span>
                      </div>
                      <Progress
                        value={selectedRequest.progress || 0}
                        className="h-3"
                      />
                    </div>

                    {/* Counselor Info */}
                    {(selectedRequest.counselor ||
                      selectedRequest.assignedCounselorInfo) && (
                      <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                          Assigned Counselor
                        </p>
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-blue-500/20">
                            {
                              (
                                selectedRequest.counselor ||
                                selectedRequest.assignedCounselorInfo
                              ).firstName?.[0]
                            }
                            {
                              (
                                selectedRequest.counselor ||
                                selectedRequest.assignedCounselorInfo
                              ).lastName?.[0]
                            }
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900 text-lg">
                              {
                                (
                                  selectedRequest.counselor ||
                                  selectedRequest.assignedCounselorInfo
                                ).firstName
                              }{' '}
                              {
                                (
                                  selectedRequest.counselor ||
                                  selectedRequest.assignedCounselorInfo
                                ).lastName
                              }
                            </p>
                            <p className="text-sm text-slate-500">
                              {
                                (
                                  selectedRequest.counselor ||
                                  selectedRequest.assignedCounselorInfo
                                ).email
                              }
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-white"
                            onClick={() => {
                              setDetailsOpen(false);
                              handleOpenMessageModal(selectedRequest);
                            }}
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Send Message
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Notes/Messages */}
                    {selectedRequest.notes?.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-3">
                          Messages
                        </p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {selectedRequest.notes
                            .filter(n => !n.isInternal)
                            .map((note, idx) => (
                              <div
                                key={idx}
                                className="p-4 bg-slate-50 rounded-xl border border-slate-100"
                              >
                                <p className="text-sm text-slate-700">
                                  {note.text}
                                </p>
                                <p className="text-xs text-slate-400 mt-2">
                                  {format(
                                    new Date(note.addedAt),
                                    'MMM d, yyyy h:mm a',
                                  )}
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="tasks" className="mt-6">
                    {getRequestTasks(selectedRequest.serviceRequestId)
                      .length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <FileText className="w-8 h-8 text-slate-300" />
                        </div>
                        <p className="text-slate-500 font-medium">
                          No tasks assigned yet
                        </p>
                        <p className="text-sm text-slate-400 mt-1">
                          Tasks will appear here once assigned
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {getRequestTasks(selectedRequest.serviceRequestId).map(
                          task => (
                            <TaskCard
                              key={task.taskId}
                              task={task}
                              onSubmit={handleSubmitTask}
                              onView={t => handleSubmitTask(t)}
                              variant="compact"
                            />
                          ),
                        )}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="messages" className="mt-6">
                    {selectedRequest.counselor ||
                    selectedRequest.assignedCounselorInfo ? (
                      <ServiceChat
                        serviceRequestId={selectedRequest.serviceRequestId}
                        counselor={
                          selectedRequest.counselor ||
                          selectedRequest.assignedCounselorInfo
                        }
                        agent={
                          selectedRequest.agent ||
                          selectedRequest.assignedAgentInfo
                        }
                      />
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <MessageSquare className="w-8 h-8 text-slate-300" />
                        </div>
                        <p className="text-slate-500 font-medium">
                          Chat not available yet
                        </p>
                        <p className="text-sm text-slate-400 mt-1">
                          Messages will be available once a counselor is
                          assigned to your request
                        </p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="history" className="mt-6">
                    {selectedRequest.statusHistory?.length > 0 ? (
                      <div className="relative">
                        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-100" />
                        <div className="space-y-4">
                          {selectedRequest.statusHistory.map((history, idx) => (
                            <div
                              key={idx}
                              className="relative flex items-start gap-4 pl-12"
                            >
                              <div className="absolute left-3 w-4 h-4 bg-white border-2 border-primary rounded-full" />
                              <div className="flex-1 p-4 bg-slate-50 rounded-xl">
                                <p className="text-sm font-medium text-slate-700">
                                  Status changed to{' '}
                                  <Badge
                                    className={`${STATUS_CONFIG[history.status]?.color} border ml-1`}
                                  >
                                    {STATUS_CONFIG[history.status]?.label}
                                  </Badge>
                                </p>
                                {history.note && (
                                  <p className="text-sm text-slate-600 mt-2">
                                    {history.note}
                                  </p>
                                )}
                                <p className="text-xs text-slate-400 mt-2">
                                  {format(
                                    new Date(history.changedAt),
                                    'MMM d, yyyy h:mm a',
                                  )}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <Clock className="w-8 h-8 text-slate-300" />
                        </div>
                        <p className="text-slate-500 font-medium">
                          No status history yet
                        </p>
                        <p className="text-sm text-slate-400 mt-1">
                          Updates will appear here as progress is made
                        </p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Task Submission Modal */}
        <Dialog open={submitTaskOpen} onOpenChange={setSubmitTaskOpen}>
          <DialogContent className="max-w-2xl border-0 shadow-2xl rounded-2xl p-0 flex flex-col max-h-[85vh]">
            {selectedTask && (
              <>
                <DialogHeader className="pb-4 px-6 pt-6 border-b shrink-0">
                  <DialogTitle className="text-xl">
                    {selectedTask.title}
                  </DialogTitle>
                  <DialogDescription className="text-slate-500 mt-1">
                    {selectedTask.description ||
                      'Complete this task by providing the required information.'}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 px-6 py-4 overflow-y-auto flex-1">
                  {selectedTask.instructions && (
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                      <p className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Instructions
                      </p>
                      <p className="text-sm text-blue-700">
                        {selectedTask.instructions}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-2 block">
                      Your Response
                    </label>
                    <Textarea
                      placeholder="Enter your response here..."
                      value={submissionText}
                      onChange={e => setSubmissionText(e.target.value)}
                      rows={4}
                      className="resize-none border-slate-200 focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-xl"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-2 block">
                      Upload Files (Optional)
                    </label>
                    <FileUpload
                      onUploadComplete={handleFileUploadComplete}
                      maxFiles={5}
                      folder="task-submissions"
                    />
                  </div>

                  {submissionFiles.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">
                        Uploaded Files
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {submissionFiles.map((file, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className="bg-slate-100 text-slate-700 px-3 py-1.5"
                          >
                            <FileText className="w-3.5 h-3.5 mr-1.5" />
                            {file.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter className="px-6 py-4 border-t gap-3 shrink-0 bg-white rounded-b-2xl">
                  <Button
                    variant="outline"
                    onClick={() => setSubmitTaskOpen(false)}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleTaskSubmission}
                    disabled={submitting}
                    className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 hover:opacity-90 shadow-lg shadow-sky-500/25 px-6"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Submit Task
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Send Message Modal */}
        <Dialog open={messageModalOpen} onOpenChange={setMessageModalOpen}>
          <DialogContent className="max-w-lg border-0 shadow-2xl rounded-2xl">
            <DialogHeader className="pb-4 border-b">
              <DialogTitle className="text-xl">Send Message</DialogTitle>
              <DialogDescription className="text-slate-500 mt-1">
                Send a message to your assigned counselor about this service
                request.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 mt-6">
              {selectedRequest &&
                (selectedRequest.counselor ||
                  selectedRequest.assignedCounselorInfo) && (
                  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white font-semibold shadow-lg shadow-blue-500/20">
                      {
                        (
                          selectedRequest.counselor ||
                          selectedRequest.assignedCounselorInfo
                        ).firstName?.[0]
                      }
                      {
                        (
                          selectedRequest.counselor ||
                          selectedRequest.assignedCounselorInfo
                        ).lastName?.[0]
                      }
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">
                        To:{' '}
                        {
                          (
                            selectedRequest.counselor ||
                            selectedRequest.assignedCounselorInfo
                          ).firstName
                        }{' '}
                        {
                          (
                            selectedRequest.counselor ||
                            selectedRequest.assignedCounselorInfo
                          ).lastName
                        }
                      </p>
                      <p className="text-sm text-slate-500">
                        Re:{' '}
                        {SERVICE_TYPE_CONFIG[selectedRequest.serviceType]
                          ?.label || selectedRequest.serviceType}
                      </p>
                    </div>
                  </div>
                )}

              <div>
                <label className="text-sm font-semibold text-slate-700 mb-2 block">
                  Your Message
                </label>
                <Textarea
                  placeholder="Type your message here..."
                  value={messageText}
                  onChange={e => setMessageText(e.target.value)}
                  rows={4}
                  className="resize-none border-slate-200 focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-xl"
                />
              </div>
            </div>

            <DialogFooter className="mt-8 pt-4 border-t gap-3">
              <Button
                variant="outline"
                onClick={() => setMessageModalOpen(false)}
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={sendingMessage}
                className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 hover:opacity-90 shadow-lg shadow-sky-500/25 px-6"
              >
                {sendingMessage ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
